import { cloneDeep } from 'lodash-es';
import React from 'react';

import { IBrandSafetyRule } from '~/src/dash/api/brand-safety-rules';
import { createUrl, IHttpResponse, restGet } from '~/src/dash/api/rest';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  extractRouteParams,
  navigate,
  parseLocation,
  RouteBrandSafetyList,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { BrandSafetyRuleEdit } from '~/src/dash/views/brand-safety-rule-edit';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

class IState {
  rule: IBrandSafetyRule; // rule to edit
  loaded: boolean; // is rule loaded
  error: string; // if not empty, display an error in the snackbar
}

export const BrandSafetyEditView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
  },
  class extends React.Component<IProps, IState> {
    state = {
      rule: {} as IBrandSafetyRule,
      loaded: false,
      error: '',
    };

    componentDidMount() {
      // get id of rule to edit
      const loc = parseLocation(this.props.location.pathname);
      const ruleID = Number(loc.brandSafety);
      // try to get the rule from session
      const rule = this.getRuleFromSession(ruleID);
      if (rule !== undefined) {
        // we got the rule from the session → set the state with it
        this.setState({
          rule,
          loaded: true,
        });
      }
      // if we cannot get the rule from the session → get it from the API
      if (rule === undefined) {
        this.getRuleFromAPI(ruleID);
      }
    }

    // navigate to the page listing all the rules
    navigateToRuleListView = (session: ISessionState, user: IUserState) => {
      navigate(RouteBrandSafetyList, extractRouteParams({ session, user }));
    };

    // gets the rule from the API
    getRuleFromAPI(ruleID: number): void {
      // build request URL
      const reqURL = createUrl(`/brandsafetyrules/${ruleID}`, this.props.session.publisher.ID);
      // make the request
      restGet<IBrandSafetyRule>(reqURL, {})
        .then((val: IHttpResponse<IBrandSafetyRule>) => {
          const rule = val.parsedBody as IBrandSafetyRule;
          // OK
          this.setState({
            rule,
            loaded: true,
          });
        })
        .catch(() => {
          // error
          this.setState(
            {
              rule: {} as IBrandSafetyRule,
              error: 'Cannot load the rule from the API. Try again later.',
              loaded: true,
            },
            () => {
              const session = cloneDeep(this.props.session);
              const user = cloneDeep(this.props.user);
              setTimeout(() => {
                this.navigateToRuleListView(session, user);
              }, 3000);
            },
          );
        });
    }

    // gets the rule from the session variable
    getRuleFromSession(ruleID: number): IBrandSafetyRule | undefined {
      if (this.props.session.brandSafetyRules === undefined) {
        return undefined;
      }
      const rules = this.props.session.brandSafetyRules.filter(
        (rule: IBrandSafetyRule) => rule.ID === ruleID,
      );
      if (rules.length >= 1) {
        return rules[0];
      }
      return undefined;
    }

    // display the edit form for this rule
    render() {
      return (
        <div>
          {this.state.loaded && (
            <BrandSafetyRuleEdit rule={this.state.rule} error={this.state.error} />
          )}
        </div>
      );
    }
  },
);
